import { z } from 'zod';
/**
 * Kulčina's drawable display — the wire format both sides agree on.
 *
 * One tower, one drawing, everybody draws on it. The geometry is pinned by
 * `Assets/generated/bg/kulcina-display.json` and must not be re-guessed here:
 * 15 columns × 32 rows, which is 480 cells, which is 60 bytes, which is 120
 * hex characters. Small enough that the whole display fits in one WebSocket
 * frame with room to spare — that is what makes live drawing affordable.
 */
export const KULCINA_COLS = 15;
export const KULCINA_ROWS = 32;
export const KULCINA_CELLS = KULCINA_COLS * KULCINA_ROWS;
/** ceil(480 / 8) bytes, hex-encoded. */
export const KULCINA_HEX_LENGTH = Math.ceil(KULCINA_CELLS / 8) * 2;
/**
 * Drawing is sent as *cell operations*, not as the whole grid, and this is the
 * one decision the whole feature rests on.
 *
 * Two people drawing at once with whole-grid pushes destroy each other: I send
 * my grid, you send yours a moment later, and yours — built before my stroke
 * arrived — erases it. With per-cell ops the two strokes merge, and the only
 * cell with a winner is a cell we both actually painted. Last writer wins per
 * cell, not per drawing.
 *
 * Reset and Clear are not an exception to this — they are an op for every
 * cell. See `kulcinaOpsFromHex` for why they stopped being a whole grid.
 */
export const kulcinaOp = z.tuple([
    z.number().int().min(0).max(KULCINA_CELLS - 1),
    z.union([z.literal(0), z.literal(1)]),
]);
/** A stroke can touch every cell at most once, so this is the true ceiling. */
export const KULCINA_MAX_OPS = KULCINA_CELLS;
/**
 * Live strokes are coalesced into one socket frame per interval while the
 * pointer is down. 100ms is below the threshold where a line looks like it is
 * being drawn rather than appearing, and caps a frantic scribbler at 10
 * frames/second instead of the ~60 the pointer actually fires at.
 */
export const KULCINA_STROKE_MS = 100;
/**
 * Server-side ceiling on `draw` frames per client per second. Well above what
 * the throttle above can produce, so only a client that has stopped honouring
 * it gets trimmed.
 */
export const KULCINA_DRAW_RATE = 25;
export const kulcinaHex = z
    .string()
    .length(KULCINA_HEX_LENGTH)
    .regex(/^[0-9a-f]+$/, 'lowercase hex only');
/** Incremental commit: the ops of one finished stroke. */
export const kulcinaOpsRequest = z.object({
    ops: z.array(kulcinaOp).min(1).max(KULCINA_MAX_OPS),
});
/**
 * Reset and Clear used to be a whole-grid PUT. They are not any more, and the
 * reason is worth keeping: a whole grid is a statement about every cell *as of
 * when you clicked*, so it silently undoes anything committed in between, and
 * it cannot be relayed over the socket the way a stroke can — the other screen
 * only catches up a full round trip later. Both showed up as "clear is slow and
 * the old drawing flashes back".
 *
 * So Reset and Clear are now an op per cell, committed exactly like a stroke.
 * Every cell, not a diff against what this client believes is on screen: a
 * client whose grid has drifted would compute a diff that leaves cells lit.
 */
export function kulcinaOpsFromHex(hex) {
    if (!kulcinaHex.safeParse(hex).success)
        return null;
    const ops = [];
    for (let i = 0; i < KULCINA_CELLS; i += 1) {
        const bit = (parseInt(hex.substr((i >> 3) * 2, 2), 16) >> (i & 7)) & 1;
        ops.push([i, bit === 1 ? 1 : 0]);
    }
    return ops;
}
/**
 * Who touched it last. Shown in the editor header, which is the only thing
 * standing between a good drawing and someone idly painting over it.
 */
export const kulcinaArtist = z.object({
    id: z.string(),
    nickname: z.string(),
});
export const kulcinaState = z.object({
    bits: kulcinaHex,
    drawnBy: kulcinaArtist.nullable(),
    /** ISO 8601, or null on a display nobody has drawn on since it was seeded. */
    drawnAt: z.string().nullable(),
    /**
     * Bumped by the database on every write, so "the newest state wins" can mean
     * newest rather than last-to-arrive.
     *
     * Without it a commit's HTTP response, delayed behind a socket frame for the
     * *next* commit, gets applied second and quietly rolls the display back — on
     * one screen only, permanently, until something else forces a refetch. That
     * is not hypothetical: it is what two browsers did at 150ms of latency.
     */
    version: z.number().int().nonnegative(),
});
/** All zeroes — what Clear sends, and the floor every op list is applied to. */
export const KULCINA_EMPTY = '0'.repeat(KULCINA_HEX_LENGTH);
/**
 * Apply ops to a hex grid, in order. Shared rather than written twice, because
 * the server applying ops differently from the client is exactly the bug that
 * would show up as "my drawing looks different on your screen".
 */
export function applyKulcinaOps(hex, ops) {
    const bytes = new Uint8Array(KULCINA_HEX_LENGTH / 2);
    for (let i = 0; i < bytes.length; i += 1)
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    for (const [index, value] of ops) {
        const byte = index >> 3;
        const mask = 1 << (index & 7);
        if (value === 1)
            bytes[byte] |= mask;
        else
            bytes[byte] &= ~mask;
    }
    let out = '';
    for (const byte of bytes)
        out += byte.toString(16).padStart(2, '0');
    return out;
}
/**
 * Hand-rolled twin of `kulcinaOp`, for the WebSocket hub.
 *
 * Routes parse with zod, as everything here does. The hub does not: it sees up
 * to `KULCINA_DRAW_RATE` frames per second per client, each carrying hundreds
 * of ops, and building zod issue objects for a shape this trivial is work with
 * nothing to show for it. The two definitions must agree, which is what
 * verify-kulcina.mjs checks them for.
 */
export function isKulcinaOps(value) {
    if (!Array.isArray(value) || value.length === 0 || value.length > KULCINA_MAX_OPS)
        return false;
    for (const op of value) {
        if (!Array.isArray(op) || op.length !== 2)
            return false;
        const [index, on] = op;
        if (!Number.isInteger(index) || index < 0 || index >= KULCINA_CELLS) {
            return false;
        }
        if (on !== 0 && on !== 1)
            return false;
    }
    return true;
}
//# sourceMappingURL=kulcina.js.map